import { useReactiveVar } from "@apollo/client";
import React, { useEffect, useState } from "react";
import Dropdown from "react-bootstrap/Dropdown";
import Offcanvas from "react-bootstrap/Offcanvas";
import {
  PlusLg,
  Ticket,
  QrCodeScan,
  Person,
  Box,
  BoxArrowLeft,
  Globe,
  Archive,
  Globe2,
  Gear,
  BarChart,
  Headset,
  TicketPerforated,
  CreditCard2Back,
  Envelope,
  FileEarmarkText,
  Tags,
} from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { MenuPaths, WhiteLabelPaths } from "../../App";
import {
  useGetMyEventsStatusCountsQuery,
  useGetOrganizerServiceInfoQuery,
} from "../../graphql/event.hooks";
import {
  createNewEventSignal,
  emailVerifyCreateEventModalVar,
  unlockWhiteLabelModalVar,
  whiteLabelJRStartModal,
  whiteLabelStep,
} from "../../graphql/globalVar/state";
import { useOrganizerWhiteLabelQuery } from "../../graphql/whiteLabel/whiteLabel.hooks";
import useGetUrlLocale from "../../hooks/useGetUrlLocale";
import { useAuthService } from "../../services/authService";
import { WhiteLaelStepEnum } from "../../types";

import MediaIcon from "./icons/MediaIcon";
import MenuNavLink from "./MenuNavLink";

export const languages = [
  { value: "en", label: "English" },
  { value: "vi", label: "Tiếng Việt" },
  { value: "de", label: "Deutsch" },
  { value: "ar", label: "عرب" },
  { value: "th", label: "ไทย" },
  { value: "ko", label: "한국인" },
  { value: "ja", label: "日本" },
  { value: "it", label: "Italiano" },
  { value: "hi", label: "हिंदी" },
  { value: "ms", label: "Bahasa Melayu" },
  { value: "id", label: "Bahasa Indonesia" },
  { value: "es", label: "Español" },
  { value: "fr", label: "Français" },
  { value: "pt", label: "Português" },
  { value: "nl", label: "Nederlands" },
  { value: "sv", label: "Svenska" },
  { value: "pl", label: "Polski" },
  { value: "uk", label: "Українська" },
];

const ME_TICKET_LINK = process.env.REACT_APP_TICKET_URL;

export default function Menu() {
  const navigate = useNavigate();
  const lng = useGetUrlLocale();
  const [searchParams] = useSearchParams();
  const { data } = useGetOrganizerServiceInfoQuery();

  const { data: countsData } = useGetMyEventsStatusCountsQuery({
    variables: { search: null },
    fetchPolicy: "cache-and-network",
  });
  const { subscriptionPlan } = useAuthService();
  const isFreePlan = subscriptionPlan === null;

  const { data: whiteLabelData, loading: whiteLabelLoading } =
    useOrganizerWhiteLabelQuery({
      fetchPolicy: "network-only",
      onCompleted(data) {
        if (data.organizer?.whiteLabel?.domain) {
          whiteLabelStep(WhiteLaelStepEnum.MediaDesign);
        } else if (data.organizer?.whiteLabel?.projectName) {
          whiteLabelStep(WhiteLaelStepEnum.CustomDomain);
        }
      },
    });
  const [show, setShow] = useState(false);

  const { t, i18n } = useTranslation();

  const currentWhiteLabelStep = useReactiveVar(whiteLabelStep);
  const locale =
    i18n.language.split("-")[0] === "en"
      ? ""
      : `/${i18n.language.split("-")[0]}`;

  const lang = i18n.language.split("-")[0];

  const changeLanguage = (lng: string) => {
    const queryParams = new URLSearchParams(location.search).toString();

    const currentPath = location.pathname
      .split("/")
      .slice(i18n.language.split("-")[0] === "en" ? 1 : 2)
      .join("/");

    const newPath = lng === "en" ? `/${currentPath}` : `/${lng}/${currentPath}`;
    const finalPath = queryParams ? `${newPath}?${queryParams}` : newPath;

    navigate(finalPath, { replace: true });
    i18n.changeLanguage(lng);
  };

  // if (data?.needVerifyEmail) {
  //   window.location.href = `${process.env.REACT_APP_TICKET_URL}${locale}/for-organizers/profile`;
  // }

  const totalEventsCount = countsData?.myEventsStatusCounts?.total ?? 0;

  const handleCreateEvent = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (data?.needVerifyEmail && totalEventsCount > 0) {
      e.preventDefault();
      emailVerifyCreateEventModalVar(true);
      return;
    }
    localStorage.removeItem("entity");
    localStorage.removeItem("poster");
    localStorage.removeItem("options");
    createNewEventSignal(createNewEventSignal() + 1);
    setShow(false);
  };

  const handleHowToStart = () => {
    if (whiteLabelLoading) return;
    setShow(false);
    navigate(`${lng}/project-settings`);
    whiteLabelJRStartModal(true);
  };

  const handleOpenUnlockWhiteLabelModal = () => {
    unlockWhiteLabelModalVar(true);
  };

  useEffect(() => {
    if (
      searchParams.get("howtostart") === "1" &&
      !whiteLabelLoading &&
      !whiteLabelData?.organizer?.whiteLabel?.domain
    ) {
      handleHowToStart();
      const path = window.location.pathname;
      navigate(path, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, navigate, whiteLabelLoading, whiteLabelData]);

  return (
    <>
      <div className="col-12 col-lg-3 col-xl-2 bg-white border-end">
        <div className="col-12 col-lg-3 col-xl-2 d-none d-lg-flex admin-left-menu p-4 h-100 flex-column justify-content-between fixed-top overflow-y-auto custom-scrollbar">
          <div className="text-dark">
            <div className=" text-center">
              <a
                href={ME_TICKET_LINK + locale + "/for-organizers/"}
                className="pb-4 d-inline-block"
              >
                <img
                  src="https://me-ticket.com/assets/img/logo.svg"
                  alt="Me ticket"
                  width="94"
                  height="30"
                />
              </a>
            </div>
            <small>
              <p className="pt-2">{t("menu.main")}</p>
              <ul className="nav flex-column">
                <li className="nav-item mb-2">
                  <MenuNavLink
                    to={`${locale}/create-event`}
                    onClick={handleCreateEvent}
                  >
                    <PlusLg className="pe-2 fs-4" /> {t("menu.create")}
                  </MenuNavLink>
                </li>
                <li className="nav-item mb-2">
                  <MenuNavLink to={`${locale}/my-events`}>
                    <Ticket className="pe-2 fs-4" /> {t("menu.events")}
                  </MenuNavLink>
                </li>
                <li className="nav-item mb-2">
                  <MenuNavLink to={`${locale}/ticket-scanner`}>
                    <QrCodeScan className="pe-2 fs-4" /> {t("menu.ticket")}
                  </MenuNavLink>
                </li>
                <li className="nav-item mb-2 ">
                  {/* <a
                    className={classNames("nav-link", {
                      "opacity-25 disabled": !isProOrganizeer || loading,
                    })}
                    href={ME_TICKET_LINK + locale + "/for-organizers/statistic"}
                  >
                    <BarChart className="pe-2 fs-4" /> {t("menu.statistics")}
                  </a> */}
                  <MenuNavLink to={`${locale}/statistics`}>
                    <BarChart className="pe-2 fs-4" />
                    {t("menu.statistics")}
                  </MenuNavLink>
                </li>
                <li className="nav-item mb-2">
                  <MenuNavLink to={`${lng}${MenuPaths.PromoCodes}`}>
                    <Tags className="me-2" /> {t("menu.promo")}
                  </MenuNavLink>
                </li>
                <li className="nav-item mb-2">
                  <MenuNavLink to={`${locale}/profile`}>
                    <Person className="pe-2 fs-4" /> {t("menu.profile")}
                  </MenuNavLink>
                </li>
                <li className="nav-item mb-2">
                  <MenuNavLink to={`${locale}/payment`}>
                    <CreditCard2Back className="pe-2 fs-4" />
                    {t("menu.Payment & Subscription")}
                  </MenuNavLink>
                </li>
                <li className="nav-item mb-2">
                  <MenuNavLink to={`${locale}/archive-events`}>
                    <Box className="pe-2 fs-4" /> {t("menu.archive")}
                  </MenuNavLink>
                </li>
                <li className="nav-item mb-3">
                  <a
                    className="nav-link "
                    target="_blank"
                    href={ME_TICKET_LINK + locale + "/organizer-support-chat"}
                    rel="noopener noreferrer"
                  >
                    <Headset className="pe-2 fs-4" /> {t("menu.support")}
                  </a>
                </li>

                <div className="mb-3 d-flex align-items-center">
                  {isFreePlan ? (
                    <button
                      type="button"
                      className="btn btn-link p-0 text-dark text-decoration-none"
                      onClick={handleOpenUnlockWhiteLabelModal}
                    >
                      {t("menu.White Label")}
                    </button>
                  ) : (
                    <div>{t("menu.White Label")}</div>
                  )}
                  {isFreePlan && (
                    <button
                      type="button"
                      className="py-1 px-2 ms-2 bg-success-bg text-success fs-9 rounded border-0"
                      onClick={handleOpenUnlockWhiteLabelModal}
                    >
                      Pro or Business
                    </button>
                  )}
                </div>
                <li className="nav-item mb-2">
                  <MenuNavLink
                    to={`${lng}${WhiteLabelPaths.ProjectSettings}`}
                    disabled={
                      currentWhiteLabelStep <
                        WhiteLaelStepEnum.ProjectSettings || isFreePlan
                    }
                  >
                    <Gear className="pe-2 fs-4" /> {t("menu.Project Settings")}
                  </MenuNavLink>
                </li>
                <li className="nav-item mb-2">
                  <MenuNavLink
                    to={`${lng}${WhiteLabelPaths.CustomDomain}`}
                    disabled={
                      currentWhiteLabelStep < WhiteLaelStepEnum.CustomDomain ||
                      isFreePlan
                    }
                  >
                    <Globe2 className="pe-2 fs-4" /> {t("menu.Custom Domain")}
                  </MenuNavLink>
                </li>
                <li className="nav-item mb-2">
                  <MenuNavLink
                    to={`${lng}${WhiteLabelPaths.MediaDesign}`}
                    disabled={
                      currentWhiteLabelStep < WhiteLaelStepEnum.MediaDesign ||
                      isFreePlan
                    }
                  >
                    <MediaIcon className="me-2 fs-4" />{" "}
                    {t("menu.Media & Design")}
                  </MenuNavLink>
                </li>
                <li className="nav-item mb-2">
                  <MenuNavLink to={`${lng}`} disabled={true}>
                    <Envelope className="me-2 fs-6" /> {t("menu.Mailing Lists")}
                  </MenuNavLink>
                </li>
                <li className="nav-item mb-2">
                  <MenuNavLink to={`${lng}`} disabled={true}>
                    <FileEarmarkText className="me-2 fs-6" />{" "}
                    {t("menu.Terms & Conditions")}
                  </MenuNavLink>
                </li>
              </ul>
            </small>
          </div>
          <ul className="nav pt-3 pb-4">
            <li className="nav-item">
              <a
                className="nav-link"
                href={ME_TICKET_LINK + locale + "/for-organizers/logout"}
              >
                <BoxArrowLeft className="fs-4 pe-2" />
                <small>{t("menu.logout")}</small>
              </a>
            </li>
            <li className="nav-item">
              <Dropdown>
                <Dropdown.Toggle
                  id="dropdown-basic"
                  variant="link"
                  className="nav-link dropdown-toggle text-uppercase"
                >
                  <Globe className="fs-4 pe-2" />
                  <small>{lang}</small>
                </Dropdown.Toggle>

                <Dropdown.Menu>
                  {languages.map((item) => (
                    <Dropdown.Item
                      key={item.value}
                      onClick={() => changeLanguage(item.value)}
                    >
                      {item.label}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </li>
          </ul>
        </div>
        <nav className="navbar bg-white fixed-top d-lg-none">
          <div className="container-fluid">
            <button
              onClick={() => setShow(true)}
              className="navbar-toggler border-0"
              type="button"
              data-bs-toggle="offcanvas"
              data-bs-target="#offcanvasNavbar"
              aria-controls="offcanvasNavbar"
            >
              <span className="navbar-toggler-icon"></span>
            </button>
            <a
              className="navbar-brand"
              href={ME_TICKET_LINK + locale + "/for-organizers/"}
            >
              <img
                src="https://me-ticket.com/assets/img/logo.svg"
                alt="Me ticket"
                width="94"
                height="30"
              />
            </a>
            <Link
              to={`${locale}/profile`}
              className="rounded-5 bg-success-bg d-flex align-items-center justify-content-center text-success fs-9 text-decoration-none"
              style={{ width: "30px", height: "30px" }}
            >
              {data?.organizer?.email?.slice(0, 2).toUpperCase()}
            </Link>
            <Offcanvas
              show={show}
              onHide={() => setShow(false)}
              placement="end"
            >
              <div className="offcanvas-header">
                <h5 className="offcanvas-title" id="offcanvasNavbarLabel">
                  <img
                    src="https://me-ticket.com/assets/img/logo.svg"
                    alt="Me ticket"
                    width="94"
                    height="30"
                  />
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShow(false)}
                ></button>
              </div>
              <div className="offcanvas-body">
                <ul className="nav flex-column pt-3 text-dark">
                  <li className="nav-item">
                    <MenuNavLink
                      to={`${locale}/create-event`}
                      onClick={handleCreateEvent}
                    >
                      <PlusLg className="me-2" /> {t("menu.create")}
                    </MenuNavLink>
                  </li>
                  <li className="nav-item">
                    <MenuNavLink to={`${locale}/my-events`}>
                      <TicketPerforated className="me-2" /> {t("menu.events")}
                    </MenuNavLink>
                  </li>
                  <li className="nav-item">
                    <MenuNavLink to={`${locale}/ticket-scanner`}>
                      <QrCodeScan className="me-2" /> {t("menu.ticket")}
                    </MenuNavLink>
                  </li>
                  <li className="nav-item">
                    {/* <a
                      className={classNames("nav-link", {
                        "opacity-25 disabled": !isProOrganizeer || loading,
                      })}
                      href={
                        ME_TICKET_LINK + locale + "/for-organizers/statistic"
                      }
                    >
                      <BarChart className="me-2" /> {t("menu.statistics")}
                    </a> */}
                    <MenuNavLink to={`${locale}/statistics`}>
                      <BarChart className="me-2" />
                      {t("menu.statistics")}
                    </MenuNavLink>
                  </li>
                  <li className="nav-item">
                    <MenuNavLink to={`${lng}${MenuPaths.PromoCodes}`}>
                      <Tags className="me-2" /> {t("menu.promo")}
                    </MenuNavLink>
                  </li>
                  <li className="nav-item">
                    <MenuNavLink to={`${locale}/profile`}>
                      <Person className="me-2" /> {t("menu.profile")}
                    </MenuNavLink>
                  </li>
                  <li className="nav-item">
                    <MenuNavLink to={`${locale}/payment`}>
                      <CreditCard2Back className="me-2" />{" "}
                      {t("menu.Payment & Subscription")}
                    </MenuNavLink>
                  </li>
                  <li className="nav-item">
                    <MenuNavLink to={`${locale}/archive-events`}>
                      <Archive className="me-2" /> {t("menu.archive")}
                    </MenuNavLink>
                  </li>
                  <li className="nav-item">
                    <a
                      className="nav-link"
                      href={ME_TICKET_LINK + locale + "/organizer-support-chat"}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Headset className="me-2" /> {t("menu.support")}
                    </a>
                  </li>
                  <div className="my-3 d-flex align-items-center">
                    {isFreePlan ? (
                      <button
                        type="button"
                        className="btn btn-link p-0 text-dark text-decoration-none"
                        onClick={handleOpenUnlockWhiteLabelModal}
                      >
                        {t("menu.White Label")}
                      </button>
                    ) : (
                      <div>{t("menu.White Label")}</div>
                    )}
                    {isFreePlan && (
                      <button
                        type="button"
                        className="py-1 px-2 ms-2 bg-success-bg text-success fs-9 rounded border-0"
                        onClick={handleOpenUnlockWhiteLabelModal}
                      >
                        Pro or Business
                      </button>
                    )}
                  </div>
                  <li className="nav-item">
                    <MenuNavLink
                      to={`${lng}${WhiteLabelPaths.ProjectSettings}`}
                      disabled={
                        currentWhiteLabelStep <
                        WhiteLaelStepEnum.ProjectSettings
                      }
                    >
                      <Gear className="me-2" /> {t("menu.Project Settings")}
                    </MenuNavLink>
                  </li>
                  <li className="nav-item">
                    <MenuNavLink
                      to={`${lng}${WhiteLabelPaths.CustomDomain}`}
                      disabled={
                        currentWhiteLabelStep < WhiteLaelStepEnum.CustomDomain
                      }
                    >
                      <Globe2 className="me-2" /> {t("menu.Custom Domain")}
                    </MenuNavLink>
                  </li>
                  <li className="nav-item">
                    <MenuNavLink
                      to={`${lng}${WhiteLabelPaths.MediaDesign}`}
                      disabled={
                        currentWhiteLabelStep < WhiteLaelStepEnum.MediaDesign
                      }
                    >
                      <MediaIcon className="me-2" /> {t("menu.Media & Design")}
                    </MenuNavLink>
                  </li>
                  <li className="nav-item">
                    <MenuNavLink to={`${lng}`} disabled={true}>
                      <Envelope className="me-2" /> {t("menu.Mailing Lists")}
                    </MenuNavLink>
                  </li>
                  <li className="nav-item">
                    <MenuNavLink to={`${lng}`} disabled={true}>
                      <FileEarmarkText className="me-2" />{" "}
                      {t("menu.Terms & Conditions")}
                    </MenuNavLink>
                  </li>
                </ul>
                <ul className="nav pt-5">
                  <li className="nav-item">
                    <a
                      className="nav-link"
                      href={ME_TICKET_LINK + locale + "/for-organizers/logout"}
                    >
                      <BoxArrowLeft className="me-1" />
                      {t("menu.logout")}
                    </a>
                  </li>
                  <li className="nav-item">
                    <Dropdown>
                      <Dropdown.Toggle
                        // variant="success"
                        id="dropdown-basic"
                        variant="link"
                        className="nav-link dropdown-toggle text-uppercase"
                      >
                        <Globe className="fs-4 me-1" />
                        <small>{lang}</small>
                      </Dropdown.Toggle>

                      <Dropdown.Menu>
                        {languages.map((item) => (
                          <Dropdown.Item
                            key={item.value}
                            onClick={() => changeLanguage(item.value)}
                          >
                            {item.label}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  </li>
                </ul>
              </div>
            </Offcanvas>
          </div>
        </nav>
      </div>
    </>
  );
}
